import React, { useRef, useState, useEffect } from "react";
import type { TestimonialGroup } from "../../data/testimonials";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TestimonialCardSkeleton } from "./TestimonialCardSkeleton";
import { TestimonialCard } from "../TestimonialCard";
import { TestimonialModal as TestimonialDetailModal } from "../TestimonialModal";

type Props = {
  group: TestimonialGroup;
};

export function TestimonialCarousel({ group }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState<typeof group.testimonials[0] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading state for better perceived performance
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  const scrollBy = (direction: "left" | "right") => {
    const node = scrollRef.current;
    if (!node) return;
    const amount = node.clientWidth * 0.8;
    node.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <section className="py-10 md:py-14">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-50">
            {group.title}
          </h2>
          <p className="text-sm md:text-base text-slate-300/80 mt-1 max-w-2xl">
            {group.subtitle}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => scrollBy("left")}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-700/70 bg-slate-900/60 hover:bg-slate-800/80 transition-all hover:scale-105 active:scale-95"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5 text-slate-200" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy("right")}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-emerald-400/70 bg-emerald-500/20 hover:bg-emerald-500/40 transition-all hover:scale-105 active:scale-95"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5 text-emerald-100" />
          </button>
        </div>
        
        {/* Mobile swipe indicator */}
        <div className="md:hidden flex items-center gap-1.5 text-xs text-slate-400">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          <span>Swipe</span>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#020817] via-[#020817]/80 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#020817] via-[#020817]/80 to-transparent" />

        <div
          ref={scrollRef}
          className="flex gap-4 md:gap-5 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory scrollbar-hide touch-pan-x"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {isLoading ? (
            // Show skeleton cards while loading
            <>
              <TestimonialCardSkeleton />
              <TestimonialCardSkeleton />
              <TestimonialCardSkeleton />
              <TestimonialCardSkeleton />
            </>
          ) : (
            // Show actual testimonial cards with fade-in animation
            group.testimonials.map((t) => (
              <div key={t.id} className="snap-start flex-shrink-0 w-[280px] md:w-[340px]">
                <TestimonialCard
                  quote={t.quote}
                  source={t.source}
                  role={t.role}
                  year={t.year}
                  context={t.context}
                  onClick={() => {
                    // For MBA study card, scroll to the section instead of opening modal
                    if (t.id === "nhs-mba-study") {
                      const section = document.getElementById("mba-nhs-study");
                      if (section) {
                        section.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    } else {
                      setSelectedTestimonial(t);
                    }
                  }}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {selectedTestimonial && (
        <TestimonialDetailModal
          isOpen={!!selectedTestimonial}
          onClose={() => setSelectedTestimonial(null)}
          testimonial={selectedTestimonial}
          groupTitle={group.title}
        />
      )}
    </section>
  );
}
